'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useUI } from '@/contexts/UIContext'
import { useChatRealtime } from '@/contexts/ChatRealtimeContext'
import { useUser } from '@/contexts/UserContext'

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const FILES_BASE = API_BASE.replace(/\/api\/?$/, '')
const ADMIN_APK_URL = `${FILES_BASE}/uploads/admin-app/farktor-admin.apk`
const LOJISTIK_APK_URL = `${FILES_BASE}/uploads/lojistik-app/farktor-lojistik.apk`

interface QuickAction {
  id: string
  icon: string
  label: string
  href?: string
  color: string
  download?: boolean
}

const BASE_QUICK_ACTIONS: QuickAction[] = [
  { id: 'order',   icon: 'fa-regular fa-cart-plus',  label: 'Sipariş', color: '#6366f1' },
  { id: 'product', icon: 'fa-regular fa-box-open',   label: 'Ürün',    color: '#0ea5e9' },
  { id: 'chat',    icon: 'fa-regular fa-message',    label: 'Mesaj',   color: '#10b981' },
  { id: 'scan',    icon: 'fa-regular fa-barcode',    label: 'Tara',    color: '#f97316' },
  { id: 'report',  icon: 'fa-regular fa-chart-line', label: 'Rapor',   color: '#a855f7' },
]

const ADMIN_APP_ACTION: QuickAction = {
  id: 'admin-app',
  icon: 'fa-regular fa-mobile-screen-button',
  label: 'Uygulama İndir',
  href: ADMIN_APK_URL,
  download: true,
  color: '#2f7bff',
}

const LOJISTIK_APP_ACTION: QuickAction = {
  id: 'lojistik-app',
  icon: 'fa-regular fa-truck-ramp-box',
  label: 'Lojistik App',
  href: LOJISTIK_APK_URL,
  download: true,
  color: '#16a34a',
}

const NAV_TABS = [
  { id: 'home',   icon: 'fa-regular fa-house',    label: 'Anasayfa' },
  { id: 'notif',  icon: 'fa-regular fa-bell',      label: 'Bildirim' },
  { id: 'notes',  icon: 'fa-regular fa-notebook',  label: 'Notlar'   },
  { id: 'chat',   icon: 'fa-regular fa-message',   label: 'Sohbet'   },
  { id: 'menu',   icon: 'fa-regular fa-grid-2',    label: 'Menü'     },
]

export function MobileNav() {
  const pathname = usePathname()
  const { setMobileSidebarOpen } = useUI()
  const { openDrawer } = useChatRealtime()
  const { user } = useUser()

  const quickActions = useMemo(() => {
    return user?.admin === 1
      ? [...BASE_QUICK_ACTIONS, ADMIN_APP_ACTION, LOJISTIK_APP_ACTION]
      : [...BASE_QUICK_ACTIONS, LOJISTIK_APP_ACTION]
  }, [user?.admin])

  const [scatterOpen, setScatterOpen] = useState(false)
  const [activeTab, setActiveTab]     = useState<string>('home')

  // Anasayfadaysa home aktif
  useEffect(() => {
    if (pathname === '/') setActiveTab('home')
  }, [pathname])

  // Blob (aktif kabarcık) animasyonu
  const innerRef    = useRef<HTMLDivElement>(null)
  const tabRefs     = useRef<(HTMLElement | null)[]>([])
  const blobRef     = useRef<HTMLDivElement>(null)
  const blobPos     = useRef<{ cx: number } | null>(null)
  const blobAnimRef = useRef<Animation | null>(null)

  const getActiveIndex = () => NAV_TABS.findIndex(t => t.id === activeTab)

  const moveBlob = (idx: number) => {
    const tab   = tabRefs.current[idx]
    const inner = innerRef.current
    const blob  = blobRef.current
    if (!tab || !inner || !blob) return

    const iRect = inner.getBoundingClientRect()
    const tRect = tab.getBoundingClientRect()
    const cx    = tRect.left - iRect.left + tRect.width / 2
    const ty    = -10

    // İlk render: animasyonsuz konumla
    if (blobPos.current === null) {
      blob.style.transform = `translateX(${cx}px) translateY(${ty}px)`
      blobPos.current = { cx }
      return
    }

    const fromCx = blobPos.current.cx
    blobPos.current = { cx }

    // Önceki animasyonu iptal et, mevcut pozisyona sabitle
    if (blobAnimRef.current) {
      blobAnimRef.current.cancel()
      blobAnimRef.current = null
    }
    blob.style.transform  = `translateX(${fromCx}px) translateY(${ty}px)`

    // Süt damlası / jelly sıçraması: squish → launch → land → bounce → settle
    const anim = blob.animate(
      [
        // 0% — başlangıç: normal daire
        { transform: `translateX(${fromCx}px) translateY(${ty}px) scale(1, 1)`,      borderRadius: '50%',        offset: 0    },
        // 14% — zemine bastırılıp fırlamadan önceki ezilme
        { transform: `translateX(${fromCx}px) translateY(${ty + 5}px) scale(1.5, 0.55)`, borderRadius: '50% / 28%', offset: 0.14 },
        // 52% — hava ortası: dikey uzamış, hedefe doğru
        { transform: `translateX(${cx}px)     translateY(${ty - 7}px) scale(0.65, 1.45)`, borderRadius: '40% / 50%', offset: 0.52 },
        // 72% — yere çarpma: yatay basık
        { transform: `translateX(${cx}px)     translateY(${ty + 6}px) scale(1.38, 0.62)`, borderRadius: '50% / 26%', offset: 0.72 },
        // 86% — küçük sekme: hafif dikey
        { transform: `translateX(${cx}px)     translateY(${ty - 3}px) scale(0.90, 1.13)`, borderRadius: '46% / 50%', offset: 0.86 },
        // 100% — yerleşti: tekrar daire
        { transform: `translateX(${cx}px)     translateY(${ty}px)     scale(1, 1)`,      borderRadius: '50%',        offset: 1    },
      ],
      { duration: 560, easing: 'ease-in-out', fill: 'forwards' }
    )

    blobAnimRef.current = anim
    anim.onfinish = () => {
      blob.style.transform = `translateX(${cx}px) translateY(${ty}px)`
      blob.style.borderRadius = '50%'
      anim.cancel()
      blobAnimRef.current = null
    }
  }

  useEffect(() => {
    setScatterOpen(false)
    const idx = getActiveIndex()
    if (idx < 0) return
    const t = setTimeout(() => moveBlob(idx), 60)
    return () => clearTimeout(t)
  }, [activeTab])

  // Scroll hide/show
  const rowRef      = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)
  const ticking     = useRef(false)

  useEffect(() => {
    const scroller = document.querySelector<HTMLElement>('.panel-content') ?? window as any
    const getY = () => (scroller instanceof Window ? scroller.scrollY : scroller.scrollTop)

    const onScroll = () => {
      if (ticking.current) return
      ticking.current = true
      requestAnimationFrame(() => {
        const row = rowRef.current
        if (row) {
          const y    = getY()
          const diff = y - lastScrollY.current
          if (diff > 6 && y > 60) {
            row.classList.add('mobile-nav-row--hidden')
          } else if (diff < -4) {
            row.classList.remove('mobile-nav-row--hidden')
          }
          lastScrollY.current = y
        }
        ticking.current = false
      })
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      {scatterOpen && (
        <div className="mobile-nav__overlay" onClick={() => setScatterOpen(false)} />
      )}

      {scatterOpen && quickActions.map((action, i) => {
        const style = { '--index': i, '--delay': `${i * 55}ms` } as React.CSSProperties
        const inner = (
          <>
            <span className="mobile-nav__scatter-icon" style={{ background: action.color }}>
              <i className={action.icon} />
            </span>
            <span className="mobile-nav__scatter-label">{action.label}</span>
          </>
        )
        if (action.href) {
          if (action.download) {
            return (
              <a key={action.id} href={action.href}
                className="mobile-nav__scatter-item"
                style={style}
                download
                rel="noopener"
                onClick={() => setScatterOpen(false)}>{inner}</a>
            )
          }
          return (
            <Link key={action.id} href={action.href} className="mobile-nav__scatter-item"
              style={style} onClick={() => setScatterOpen(false)}>{inner}</Link>
          )
        }
        return (
          <button key={action.id} className="mobile-nav__scatter-item"
            style={style} onClick={() => setScatterOpen(false)}>{inner}</button>
        )
      })}

      <div className="mobile-nav-row" ref={rowRef}>

        <button
          className={`mobile-nav__fab${scatterOpen ? ' mobile-nav__fab--open' : ''}`}
          onClick={() => setScatterOpen(v => !v)}
          aria-label={scatterOpen ? 'Kapat' : 'Hızlı işlem'}
          aria-expanded={scatterOpen}
        >
          <i className="fa-regular fa-plus mobile-nav__fab-icon" />
        </button>

        <nav className="mobile-nav" aria-label="Ana navigasyon">
          <div className="mobile-nav__inner" ref={innerRef}>

            {/* Kayan blob */}
            <div className="mobile-nav__blob" ref={blobRef} />

            {NAV_TABS.map((tab, i) => {
              const active = tab.id === activeTab
              const cls    = `mobile-nav__item${active ? ' mobile-nav__item--active' : ''}`
              const setRef = (el: HTMLElement | null) => { tabRefs.current[i] = el }

              const content = (
                <>
                  <span className="mobile-nav__bubble">
                    <i className={tab.icon} />
                  </span>
                  <span className="mobile-nav__label">{tab.label}</span>
                </>
              )

              if (tab.id === 'home') return (
                <Link key={tab.id} href="/" prefetch={false}
                  className={cls} ref={setRef as any}
                  onClick={() => setActiveTab('home')}>
                  {content}
                </Link>
              )

              return (
                <button key={tab.id} className={cls} ref={setRef as any}
                  onClick={() => {
                    if (tab.id === 'menu') { setMobileSidebarOpen(true); return }
                    if (tab.id === 'chat') { openDrawer(); return }
                    setActiveTab(tab.id)
                  }}>
                  {content}
                </button>
              )
            })}

          </div>
        </nav>
      </div>
    </>
  )
}
